import React, { useState, useMemo } from 'react';
import { Form, Row, Col } from 'reactstrap';
import { ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray } from '../../../../../../../../../GlobalComponents/Helpers';

const COLUMN_ROLES = [
    { label: 'Ignore', value: 'ignore' },
    { label: 'Source Parent', value: 'source_parent' },
    { label: 'Source Value', value: 'source_value' },
    { label: 'Destination Chain', value: 'destination_chain' },
];

/**
 * LoadCompositeMappingsModal — Smart importer for composite value mappings.
 *
 * Allows pasting tabular data and defining what each column represents,
 * then resolves source values + destination chain and creates mappings.
 */
const LoadCompositeMappingsModal = ({
    sourceResourceId,
    sourceResourceName,
    actionRegister,
    actionErrors,
    actionControl,
    actionSetValue,
    actionReset,
    handleDMTAction,
    getResourceValueMappings,
}) => {
    const [step, setStep] = useState(1); // 1 = paste data, 2 = define columns
    const [parsedHeaders, setParsedHeaders] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [headersIncluded, setHeadersIncluded] = useState(true);
    const [allowOverwrite, setAllowOverwrite] = useState(false);
    const [preview, setPreview] = useState(null);

    const resetState = () => {
        setStep(1);
        setParsedHeaders([]);
        setParsedRows([]);
        setColumnDefs([]);
        setHeadersIncluded(true);
        setAllowOverwrite(false);
        setPreview(null);
        actionSetValue('compositeImportData' + sourceResourceId, '');
    };

    const handleParseData = () => {
        const raw = actionControl._formValues?.['compositeImportData' + sourceResourceId] || '';
        if (!raw.trim()) return ToastBuilder('error', 'No data to parse');

        const lines = raw.trim().split('\n');
        if (lines.length < 2) return ToastBuilder('error', 'Need at least 2 rows (header + data)');

        const firstRow = lines[0].split('\t');
        const dataRows = lines.slice(headersIncluded ? 1 : 0).map(l => l.split('\t'));

        const headers = headersIncluded
            ? firstRow.map(h => h.trim())
            : firstRow.map((_, i) => `Column ${i + 1}`);

        // Auto-detect column roles based on header names
        const autoDefs = headers.map((h, idx) => {
            const lower = h.toLowerCase();
            if (lower.includes('ignore') || lower === 'source' || lower.includes('board type')) return { role: 'ignore', level: null };
            if (lower.includes('parent') || lower.includes('group') || lower.includes('board')) return { role: 'source_parent', level: null };
            if (lower.includes('source') || lower.includes('item') || lower.includes('category') && idx <= 3) return { role: 'source_value', level: null };
            return { role: 'ignore', level: null };
        });

        // If no source_value detected, try smarter heuristics
        const hasSourceValue = autoDefs.some(d => d.role === 'source_value');
        if (!hasSourceValue && autoDefs.length > 2) {
            // Assume: first non-ignore after source_parent is source_value
            let foundParent = false;
            for (let i = 0; i < autoDefs.length; i++) {
                if (autoDefs[i].role === 'source_parent') { foundParent = true; continue; }
                if (foundParent && autoDefs[i].role === 'ignore') {
                    autoDefs[i] = { role: 'source_value', level: null };
                    break;
                }
            }
        }

        // Remaining unassigned columns after source_value become destination_chain levels
        let chainLevel = 0;
        let pastSourceValue = false;
        for (let i = 0; i < autoDefs.length; i++) {
            if (autoDefs[i].role === 'source_value') { pastSourceValue = true; continue; }
            if (pastSourceValue && autoDefs[i].role === 'ignore') {
                autoDefs[i] = { role: 'destination_chain', level: chainLevel++ };
            }
        }

        setParsedHeaders(headers);
        setParsedRows(dataRows);
        setColumnDefs(autoDefs);
        setStep(2);
    };

    const handleColumnRoleChange = (idx, newRole) => {
        const updated = [...columnDefs];
        if (newRole === 'destination_chain') {
            // Auto-assign next available level
            const existingLevels = updated.filter(d => d.role === 'destination_chain').map(d => d.level);
            const nextLevel = existingLevels.length > 0 ? Math.max(...existingLevels) + 1 : 0;
            updated[idx] = { role: newRole, level: nextLevel };
        } else {
            updated[idx] = { role: newRole, level: null };
        }
        setColumnDefs(updated);
    };

    const handleChainLevelChange = (idx, newLevel) => {
        const updated = [...columnDefs];
        updated[idx] = { ...updated[idx], level: parseInt(newLevel) };
        setColumnDefs(updated);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate: must have source_value and at least one destination_chain
        const hasSource = columnDefs.some(d => d.role === 'source_value');
        const hasChain = columnDefs.some(d => d.role === 'destination_chain');

        if (!hasSource) return ToastBuilder('error', 'Must define a Source Value column');
        if (!hasChain) return ToastBuilder('error', 'Must define at least one Destination Chain column');

        // Build column config for the backend
        const columnsConfig = columnDefs.map(d => {
            if (d.role === 'destination_chain') return { role: d.role, level: d.level };
            return { role: d.role };
        });

        // Rebuild raw data from parsed rows (excluding header if included)
        const rawData = parsedRows.map(row => row.join('\t')).join('\n');

        handleDMTAction('load-composite-mappings', sourceResourceId, (results) => {
            if (results?.data?.success) {
                const { created, updated, skipped } = results.data;
                ToastBuilder('success', results.data.message);
                if (skipped && skipped.length > 0) {
                    const reasons = skipped.slice(0, 5).map(s => `Row ${s.row}: ${s.reason}`);
                    console.warn('Skipped rows:', skipped);
                    ToastBuilder('info', `${skipped.length} skipped. Check console for details.`);
                }
                getResourceValueMappings();
                resetState();
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to load composite mappings');
            }
        }, {
            data: rawData,
            columns: columnsConfig,
            allowOverwrite,
            headersIncluded: false, // We already stripped headers in parsedRows
        });
    };

    const chainLevelOptions = useMemo(() => {
        return Array.from({ length: 5 }, (_, i) => ({ label: `Level ${i + 1}`, value: i }));
    }, []);

    const sampleRows = parsedRows.slice(0, 3);

    return (
        <ModalButtonBuilder title='Import Composite' className='btn-outline-danger'
            formId={'load-composite-mappings-form-' + sourceResourceId}
            modalTitle={`Import Composite Mappings — ${sourceResourceName}`}
            style={{ minWidth: '700px', maxWidth: '800px' }}
            onClick={resetState}
            onSubmit={step === 2 ? 'Import Mappings' : false}
            onCancelLabel={step === 2 ? 'Back' : 'Cancel'}
            onCancel={step === 2 ? () => setStep(1) : undefined}
            body={
                <Form id={'load-composite-mappings-form-' + sourceResourceId} onSubmit={handleSubmit}>
                    {step === 1 && (
                        <>
                            <p>Paste tab-separated data from your spreadsheet.</p>
                            <ul style={{ fontSize: '12px', color: '#636E72' }}>
                                <li>Include column headers in the first row for auto-detection</li>
                                <li>Columns will be mapped to: Source Parent, Source Value, and Destination Chain levels</li>
                                <li>Unused columns can be marked as "Ignore"</li>
                            </ul>
                            <br />
                            <div className="mb-3">
                                <label className="form-check-label me-3">
                                    <input type="checkbox" className="form-check-input me-1"
                                        checked={headersIncluded}
                                        onChange={(e) => setHeadersIncluded(e.target.checked)}
                                    />
                                    First row contains headers
                                </label>
                            </div>
                            <textarea
                                className="form-control"
                                style={{ height: '200px', fontFamily: 'monospace', fontSize: '12px' }}
                                placeholder={'Source\tBoard Type\tBoard Name\tBoard Item\tCategory\tSubCategory\nignore\tignore\tUnified - MS Help Desk\t1 - 3CX\tSoftware & Applications\tRingCentral'}
                                {...actionRegister('compositeImportData' + sourceResourceId)}
                            />
                            <ButtonBuilder
                                label="Parse & Configure Columns"
                                className="btn-primary mt-3"
                                type="button"
                                useSpinner={false}
                                onClick={handleParseData}
                            />
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <p style={{ fontSize: '13px' }}>
                                Define what each column represents. <strong>{parsedRows.length}</strong> data row(s) detected.
                            </p>

                            <div className="table-responsive mb-3" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                <table className="table table-sm table-bordered" style={{ fontSize: '11px' }}>
                                    <thead>
                                        <tr>
                                            {parsedHeaders.map((h, i) => (
                                                <th key={i} style={{ minWidth: '120px', background: '#f8f9fa' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sampleRows.map((row, ri) => (
                                            <tr key={ri}>
                                                {row.map((cell, ci) => (
                                                    <td key={ci} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <h6>Column Roles</h6>
                            <div style={{ fontSize: '12px' }}>
                                {parsedHeaders.map((header, idx) => (
                                    <Row key={idx} className="mb-1 align-items-center">
                                        <Col sm={3} className="text-truncate" title={header}>
                                            <strong>{header}</strong>
                                        </Col>
                                        <Col sm={4}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={columnDefs[idx]?.role || 'ignore'}
                                                onChange={(e) => handleColumnRoleChange(idx, e.target.value)}
                                            >
                                                {COLUMN_ROLES.map(r => (
                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                ))}
                                            </select>
                                        </Col>
                                        <Col sm={3}>
                                            {columnDefs[idx]?.role === 'destination_chain' && (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={columnDefs[idx]?.level ?? 0}
                                                    onChange={(e) => handleChainLevelChange(idx, e.target.value)}
                                                >
                                                    {chainLevelOptions.map(o => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </Col>
                                        <Col sm={2} className="text-muted" style={{ fontSize: '10px' }}>
                                            {sampleRows[0]?.[idx] || ''}
                                        </Col>
                                    </Row>
                                ))}
                            </div>

                            <hr />
                            <div className="mb-2">
                                <label className="form-check-label">
                                    <input type="checkbox" className="form-check-input me-1"
                                        checked={allowOverwrite}
                                        onChange={(e) => setAllowOverwrite(e.target.checked)}
                                    />
                                    Allow overwriting existing mappings
                                </label>
                            </div>
                        </>
                    )}
                </Form>
            }
        />
    );
};

export default LoadCompositeMappingsModal;
